import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FiHeart, FiStar, FiZap, FiActivity, FiShield, FiSun, FiBookOpen } from 'react-icons/fi';
import useScrollReveal from '../hooks/useScrollReveal';
import WaveDivider from '../components/WaveDivider';
import PageHero from '../components/PageHero';

const Section = styled.section`
  padding: ${({ $py }) => $py || '100px'} 24px;
  background: ${({ $bg }) => $bg || 'var(--cream)'};
`;

const Container = styled.div`
  max-width: ${({ $maxWidth }) => $maxWidth || '800px'};
  margin: 0 auto;
`;

const SectionLabel = styled.span`
  display: block;
  font-family: 'Inter', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--sage-muted);
  margin-bottom: 16px;
`;

const SectionHeading = styled.h2`
  font-family: 'Lora', serif;
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 32px;
  line-height: 1.3;
`;

const BodyText = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  color: var(--text-body);
  line-height: 1.8;
  margin-bottom: 20px;
`;

const PopulationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 28px;
  margin-top: 48px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    max-width: 480px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const PopulationCard = styled.div`
  background: white;
  padding: 32px;
  border-radius: 8px;
  box-shadow: 0 1px 4px var(--shadow);
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-3px);
  }
`;

const CardIcon = styled.div`
  font-size: 2rem;
  color: var(--sage);
  margin-bottom: 12px;
  display: flex;
`;

const CardTitle = styled.h3`
  font-family: 'Lora', serif;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 8px;
`;

const CardText = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  color: var(--text-muted);
  line-height: 1.7;
`;

const FitList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FitItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px 0;
  border-bottom: 1px solid var(--border-light);
  font-family: 'Inter', sans-serif;
  font-size: 0.95rem;
  color: var(--text-body);
  line-height: 1.7;

  &:last-child {
    border-bottom: none;
  }
`;

const FitDot = styled.span`
  width: 10px;
  height: 10px;
  min-width: 10px;
  border-radius: 50%;
  background: var(--sage);
  margin-top: 7px;
`;

const CTASection = styled.section`
  background: var(--mist);
  padding: 100px 24px;
  text-align: center;
`;

const CTAInner = styled.div`
  max-width: 600px;
  margin: 0 auto;
`;

const CTAHeading = styled.h2`
  font-family: 'Lora', serif;
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 16px;
`;

const CTASubtitle = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 1rem;
  color: var(--text-body);
  line-height: 1.7;
  margin-bottom: 32px;
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background: var(--sage);
  color: white;
  padding: 14px 36px;
  border-radius: 4px;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-size: 0.8rem;
  transition: background 0.3s ease;

  &:hover {
    background: var(--sage-muted);
  }
`;

const populations = [
  {
    icon: <FiHeart />,
    title: 'Relationships & Families',
    text: 'Specialized training in couples and family therapy, working with all relationship configurations \u2014 couples, polycules, chosen families, siblings, roommates, friends, and more.',
  },
  {
    icon: <FiStar />,
    title: 'LGBTQ+ Folks',
    text: 'As a queer therapist, I am deeply committed to affirming and supporting LGBTQ+ individuals and relationships.',
  },
  {
    icon: <FiZap />,
    title: 'Neurodivergent People',
    text: 'Particularly those with later-in-life awareness or suspicion of ADHD and Autism.',
  },
  {
    icon: <FiActivity />,
    title: 'Chronic Pain & Illness',
    text: 'Supporting people who navigate the challenges of living with chronic conditions.',
  },
  {
    icon: <FiShield />,
    title: 'Trauma, Grief & Anxiety',
    text: 'Working with acute and complex trauma, grief, and the anxiety that comes with them.',
  },
  {
    icon: <FiSun />,
    title: 'Perinatal Journey',
    text: 'Support at any stage \u2014 pre-pregnancy through the first years of parenthood, and beyond.',
  },
  {
    icon: <FiBookOpen />,
    title: 'Therapists & Healers',
    text: 'Therapists and other healers navigating the unique challenges of caring for others.',
  },
];

const fitItems = [
  'Are feeling unfulfilled, anxious, or distressed in relationships, even ones that you really value',
  'Desire more closeness in your relationship, even if things are going well',
  'Feel stuck in grief, trauma, anxiety, people pleasing, perfectionism, fear of failure, and avoiding things you desperately want',
  "Feel like you're missing something everyone else seems to be getting",
  'Experience a sense of feeling out of place in the world',
  'Have a desire to balance showing up for your family and community while also being able to take care of yourself',
];

const WhoIWorkWith = () => {
  useScrollReveal();

  return (
    <>
      <PageHero
        label="Who I Work With"
        title="Is This a Good Fit?"
        subtitle="Couples, family, and individual therapy for people looking to chart a different course."
      />

      <WaveDivider topColor="#e4e8e1" bottomColor="#eef0eb" />

      {/* Intro */}
      <Section $bg="var(--cream)">
        <Container $maxWidth="1000px" className="fade-in">
          <div>
            <BodyText>
              I work with all different kinds of people! Arguably the
              important criteria is if we feel like a good fit for each other. This
              is why I offer a free 15 minute phone consultation before booking
              with me.
            </BodyText>
            <BodyText>
              Every therapeutic relationship is a step forward into vulnerability.
              I want you to feel like you're not taking that step blindly.
            </BodyText>
          </div>
        </Container>
      </Section>

      <WaveDivider topColor="#eef0eb" bottomColor="#e4e8e1" />

      {/* Populations */}
      <Section $bg="var(--frost)">
        <Container $maxWidth="1000px">
          <SectionLabel className="fade-in">I Frequently Work With</SectionLabel>
          <SectionHeading className="fade-in">
            Communities &amp; Experiences
          </SectionHeading>
          <PopulationsGrid>
            {populations.map((item, index) => (
              <PopulationCard
                key={item.title}
                className={`fade-in delay-${(index % 3) + 1}`}
              >
                <CardIcon>{item.icon}</CardIcon>
                <CardTitle>{item.title}</CardTitle>
                <CardText>{item.text}</CardText>
              </PopulationCard>
            ))}
          </PopulationsGrid>
        </Container>
      </Section>

      <WaveDivider topColor="#e4e8e1" bottomColor="#eef0eb" />

      {/* Good Fit */}
      <Section $bg="var(--cream)">
        <Container className="fade-in">
          <SectionHeading>
            You may be a good fit if you:
          </SectionHeading>
          <FitList>
            {fitItems.map((item) => (
              <FitItem key={item}>
                <FitDot />
                {item}
              </FitItem>
            ))}
          </FitList>
        </Container>
      </Section>

      <WaveDivider topColor="#eef0eb" bottomColor="#d8ddd5" />

      {/* CTA */}
      <CTASection>
        <CTAInner className="fade-in">
          <CTAHeading>Let's See If We're a Good Fit</CTAHeading>
          <CTASubtitle>
            Book a free 15-minute phone consultation — no commitment, just a
            conversation.
          </CTASubtitle>
          <CTAButton to="/contact">Schedule a Consultation</CTAButton>
        </CTAInner>
      </CTASection>
    </>
  );
};

export default WhoIWorkWith;
